// @flow

import React from 'react';
import { graphql } from 'gatsby';
import styled, { css } from 'styled-components';

import type { Images } from 'hooks/use-images';
import WebPLoader from './WebPLoader';
import OtherImageLoader from './OtherImageLoader';
import { useIntersectionObserver } from 'hooks';
import { absolute } from 'styles';

const Wrapper = styled.div`
  ${props =>
    props.fitContainer
      ? css`
          ${absolute()};
        `
      : css`
          position: relative;
          width: 100%;
        `}
`;

const Observer = styled.div`
  position: absolute;
  top: ${props => (props.loadEarly ? '-60px' : 0)};
  right: 0;
  bottom: 0;
  left: 0;
`;

export type Props = { image: Images, fitContainer?: boolean, alt?: string, overrideLoading?: boolean };

const ImageLoader = (props: Props) => {
  const { image, fitContainer, overrideLoading = false } = props;
  const [elRef, visible] = useIntersectionObserver();
  return (
    <Wrapper fitContainer={fitContainer}>
      <Observer ref={elRef} loadEarly={!image.hasAspectRatio} />
      {image.hasAspectRatio && <WebPLoader visible={visible} overrideLoading={overrideLoading} {...props} />}
      {!image.hasAspectRatio && <OtherImageLoader visible={visible} {...props} />}
    </Wrapper>
  );
};

export default ImageLoader;

export const query = graphql`
  fragment ImageToWebP on File {
    name
    hasAspectRatio: relativePath
    childImageSharp {
      originalWebp: fixed(width: 2000, quality: 60, toFormat: WEBP) {
        src
        aspectRatio
        width
      }
      original: fixed(width: 2000, quality: 60) {
        src
        aspectRatio
        width
        base64
      }
    }
  }

  fragment ImageToWebP600 on File {
    name
    hasAspectRatio: relativePath
    childImageSharp {
      originalWebp: fixed(width: 600, quality: 60, toFormat: WEBP) {
        src
        aspectRatio
        width
      }
      original: fixed(width: 600, quality: 60) {
        src
        aspectRatio
        width
        base64
      }
    }
  }

  fragment OtherImage on File {
    name
    extension
    publicURL
  }
`;
